import type { Theme, ThemeColors } from '@utazzo/shared';

/**
 * The theme as CSS text, for a server render.
 *
 * `applyTheme` sets the same custom properties from an effect, which is too
 * late for HTML that was rendered on the server: the first paint would show
 * the stylesheet defaults and then flip to the CMS theme. Inlining this in
 * `<head>` makes the very first paint the branded one.
 *
 * Deliberately NOT a `'use client'` module - a Server Component has to be able
 * to call it, which it cannot do with anything exported from `./index`.
 */

export const THEME_COLOR_CSS_VARS: Record<keyof ThemeColors, string> = {
  background: '--background',
  foreground: '--foreground',
  primary: '--primary',
  primaryForeground: '--primary-foreground',
  secondary: '--secondary',
  secondaryForeground: '--secondary-foreground',
  accent: '--accent',
  accentForeground: '--accent-foreground',
  muted: '--muted',
  mutedForeground: '--muted-foreground',
  destructive: '--destructive',
  destructiveForeground: '--destructive-foreground',
  success: '--success',
  warning: '--warning',
  border: '--border',
  ring: '--ring',
};

/** Values come from the CMS - keep anything that could close the rule or the tag out. */
const safe = (value: string): string => value.replace(/[<>{};]/g, '');

const colorDeclarations = (colors: ThemeColors): string =>
  (Object.entries(THEME_COLOR_CSS_VARS) as Array<[keyof ThemeColors, string]>)
    .map(([key, cssVar]) => `${cssVar}:${safe(colors[key])};`)
    .join('');

export const themeCssText = (theme: Theme): string => {
  const shared =
    `--radius:${safe(theme.radius)};` +
    `--font-sans:${safe(theme.fonts.sans)};` +
    `--font-heading:${safe(theme.fonts.heading)};` +
    `--font-mono:${safe(theme.fonts.mono)};`;

  // `system` cannot be resolved on the server, so the visitor's own preference
  // picks the palette; `applyTheme` takes over (and sets the `dark` class) once
  // the page hydrates.
  if (theme.defaultMode === 'system') {
    return (
      `:root{${colorDeclarations(theme.colors.light)}${shared}}` +
      `@media (prefers-color-scheme:dark){:root{${colorDeclarations(theme.colors.dark)}}}`
    );
  }
  return `:root{${colorDeclarations(theme.colors[theme.defaultMode])}${shared}}`;
};
